import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ToastProvider, useToast } from '../ui/Toast';
import { loadSettings } from '../settings/settingsStore';
import { psApi } from './psApi';
import PsTree from './PsTree';
import PsProductPicker, { readRecent } from './PsProductPicker';
import { PsBaselines, PsBranches, PsDashboard, PsParts } from './PsViews';
import { NewProductModal, useConfirm } from './PsModals';
import PsImport from './PsImport';
import { PsBtnIcon } from './psIcons';
import './ProductStructures.css';

const COMPACT_KEY = 'ps.dashboard.compact';

const TITLES = {
  'ps-dashboard': ['Dashboard', 'One card per product. Parts, revisions and status are read live from Orcanos; the structure is kept here.'],
  'ps-structures': ['Product structures', ''],
  'ps-import': ['Import BOM file', 'An Excel or CSV BOM into a product structure. Parts come from Orcanos — missing ones are created there; the structure is kept here.'],
  'ps-parts': ['Part usage', 'The Orcanos parts used in product structures, and where each one is used.'],
  'ps-baselines': ['Baselines', 'A frozen copy of a product’s structure, and a compare between any two points.'],
  'ps-branches': ['Branches', 'Variants and change proposals of a product’s structure.'],
};
const NEEDS_ROOT = new Set(['ps-structures', 'ps-baselines', 'ps-branches']);
// Views whose one grid fills the screen and scrolls alone — the page stays put (2026-11-02).
const FIT_VIEWS = new Set(['ps-structures', 'ps-parts']);

/**
 * Product structures (2026-10-28) — design/DESIGN_BOM_HIERARCHY.md, as built docs/BOM_STRUCTURES.md.
 * Its own page beside the hardware BOM tree, never inside it: the Orcanos structure and the four
 * places that agree on it (Critical Note #61) are not touched. State that should survive a reload
 * (product, branch, located item, compare sides) lives in the URL.
 */
export default React.memo(function ProductStructures({ view, onSessionExpired }) {
  return (
    <ToastProvider>
      <Inner view={view} onSessionExpired={onSessionExpired} />
    </ToastProvider>
  );
});

function Inner({ view, onSessionExpired }) {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [state, setState] = useState({ status: 'loading' });
  const [newProduct, setNewProduct] = useState(false);
  const [compact, setCompact] = useState(() => {   // dashboard cards folded to one line per product
    try { return localStorage.getItem(COMPACT_KEY) === '1'; } catch { return false; }
  });
  const toggleCompact = () => setCompact((c) => {
    try { localStorage.setItem(COMPACT_KEY, c ? '0' : '1'); } catch { /* private window */ }
    return !c;
  });
  const [confirmEl, ask] = useConfirm();
  const expiredRef = useRef(onSessionExpired);
  expiredRef.current = onSessionExpired;
  const onAuth = useCallback((err) => {
    if (err && err.httpCode === 401) { expiredRef.current?.(); return true; }
    return false;
  }, []);

  const loadRoots = useCallback(async () => {
    try {
      const [settings, r] = await Promise.all([loadSettings(), psApi.roots()]);
      setState({ status: 'ready', roots: r.roots, canEdit: r.can_edit, partPerm: r.part_perm || '', admin: !!settings?.can_edit });
    } catch (e) {
      if (onAuth(e)) return;
      if (e.httpCode === 409) setState({ status: 'off', message: e.message });
      else setState({ status: 'error', error: e.message });
    }
  }, [onAuth]);
  useEffect(() => { loadRoots(); }, [loadRoots]);

  const go = useCallback((v, p = {}) => {
    const qs = new URLSearchParams(Object.entries(p).filter(([, x]) => x));
    navigate(`/bom/${v}${qs.toString() ? `?${qs}` : ''}`);
  }, [navigate]);

  const [title, sub] = TITLES[view] || TITLES['ps-structures'];
  if (state.status === 'loading') return <div className="ps-page"><div className="ps-muted">Loading…</div></div>;
  if (state.status === 'error') return <div className="ps-page"><div className="ps-error" role="alert">{state.error}</div></div>;
  if (state.status === 'off') {
    return (
      <div className="ps-page">
        <h1>{title}</h1>
        <div className="ps-empty">
          <p>{state.message}</p>
          <p className="ps-muted">The BOM Viewer keeps working exactly as it does today without it.</p>
          <p style={{ marginTop: 12 }}><Link className="ps-btn ps-btn--ghost" to="/settings">Admin → Settings</Link></p>
        </div>
      </div>
    );
  }

  const roots = state.roots;
  const root = roots.some((r) => r.node === params.get('root')) ? params.get('root')
    : (readRecent().find((n) => roots.some((r) => r.node === n)) || roots[0]?.node);
  const rootRow = roots.find((r) => r.node === root);
  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next, { replace: true });
  };
  const pickRoot = (node) => setParams(new URLSearchParams({ root: node }), { replace: true });

  return (
    <div className={`ps-page${FIT_VIEWS.has(view) ? ' ps-page--fit' : ''}`}>
      <div className="ps-head">
        <div>
          <div className="ps-head-t">
            <h1>{title}</h1>
            {view === 'ps-dashboard' && roots.length > 0 && (
              <button type="button" className="ps-fold" onClick={toggleCompact} aria-expanded={!compact}
                aria-label={compact ? 'Expand the product cards' : 'Minimize the product cards'}
                title={compact ? 'Show the full product cards' : 'Fold the product cards to one line each'}>
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.4"
                  strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                  <path d={compact ? 'M6 9l6 6 6-6' : 'M6 15l6-6 6 6'} />
                </svg>
              </button>
            )}
          </div>
          {sub && !(view === 'ps-dashboard' && compact) && <p className="ps-muted">{sub}</p>}
        </div>
        {view === 'ps-structures' && state.canEdit && (
          <div className="ps-row">
            <button type="button" className="ps-btn ps-btn--ghost" onClick={() => go('ps-import', { import: 'new' })}>Import BOM file</button>
            <button type="button" className="ps-btn ps-btn--ghost" onClick={() => setNewProduct(true)}>+ New product</button>
          </div>
        )}
        {(view === 'ps-baselines' || view === 'ps-branches') && (
          <div className="ps-row">
            <button type="button" className="ps-btn ps-btn--ghost" onClick={() => go('ps-structures', { root })}>← Product structures</button>
          </div>
        )}
      </div>

      {view === 'ps-structures' && !state.canEdit && (
        <div className="ps-muted" role="note" style={{ marginBottom: 12 }}>
          View only: your Orcanos rights on <b>Part (PRT)</b> in the parts project are <b>{state.partPerm}</b>, without <b>U</b> (Edit),
          so you can't add or change product structures. Ask your Orcanos administrator for Edit on Part.
        </div>
      )}

      {NEEDS_ROOT.has(view) && roots.length > 0 && (
        <div className="ps-row">
          <span className="ps-lbl">Product</span>
          <PsProductPicker roots={roots} root={root} onPick={pickRoot} />
          {view === 'ps-structures' && state.canEdit && rootRow && (
            <button type="button" className="ps-btn ps-btn--icon-danger"
              title={`Remove ${rootRow.key} from the product list`} aria-label="Remove from list"
              onClick={async () => {
                if (!(await ask({
                  title: `Remove ${rootRow.key} from the product list?`,
                  message: `${rootRow.key} will no longer be listed as a product. Its parts stay in Orcanos, and its lines stay (a sub-assembly may be used in other products).`,
                  confirmLabel: 'Remove from list', typeWord: 'DELETE',
                }))) return;
                try { await psApi.removeRoot(root); showToast(`${rootRow.key} removed from the product list.`); await loadRoots(); }
                catch (e) { if (!onAuth(e)) showToast(e.message, 'error'); }
              }}><PsBtnIcon name="trash" size={16} /></button>
          )}
        </div>
      )}

      {NEEDS_ROOT.has(view) && roots.length === 0 && (
        <div className="ps-empty">
          <p>No product structures yet.</p>
          <p className="ps-muted">{state.canEdit
            ? <>Use <b>+ New product</b> on Product structures to pick the Orcanos part at the top of a structure.</>
            : 'Ask someone with Edit rights on parts in Orcanos to add the first product.'}</p>
          {view !== 'ps-structures' && <p style={{ marginTop: 12 }}><button type="button" className="ps-btn ps-btn--ghost" onClick={() => go('ps-structures')}>Go to Product structures</button></p>}
        </div>
      )}

      {view === 'ps-dashboard' && <PsDashboard go={go} onAuth={onAuth} compact={compact} />}
      {view === 'ps-parts' && <PsParts go={go} onAuth={onAuth} />}
      {view === 'ps-import' && !state.canEdit && (
        <div className="ps-empty"><p>Importing needs Edit (<b>U</b>) on Part (PRT) in the parts project — your rights are <b>{state.partPerm}</b>.</p></div>
      )}
      {view === 'ps-import' && state.canEdit && (
        <PsImport roots={roots} importId={params.get('import') || ''} go={go} showToast={showToast} onAuth={onAuth} onDone={loadRoots}
          setImportId={(id) => setParams(id ? new URLSearchParams({ import: id }) : new URLSearchParams(), { replace: !!id && id !== 'new' })} />
      )}
      {view === 'ps-structures' && root && (
        <PsTree key={root} root={root} branchId={params.get('branch') || ''} locate={params.get('locate') || ''}
          onLocate={(node, r) => {
            if (r && r !== root) go('ps-structures', { root: r, locate: node });
            else setParam('locate', node);
          }}
          onOpen={(v) => go(v, { root })}
          onBranch={(id) => { const n = new URLSearchParams(params); if (id) n.set('branch', id); else n.delete('branch'); n.delete('locate'); setParams(n, { replace: true }); }}
          showToast={showToast} onAuth={onAuth} />
      )}
      {view === 'ps-baselines' && root && (
        <PsBaselines key={root + (params.get('a') || '') + (params.get('b') || '')} root={root} rootRow={rootRow} canEdit={state.canEdit}
          initialA={params.get('a') || ''} initialB={params.get('b') || ''} showToast={showToast} onAuth={onAuth} />
      )}
      {view === 'ps-branches' && root && (
        <PsBranches key={root} root={root} rootRow={rootRow} go={go} showToast={showToast} onAuth={onAuth} />
      )}

      {newProduct && (
        <NewProductModal existing={roots.map((r) => r.node)} onClose={() => setNewProduct(false)}
          onAdd={async (item) => {
            await psApi.addRoot(item.node);
            setNewProduct(false);
            showToast(`${item.key} added. Use + Child on its row to add its parts.`);
            await loadRoots();
            pickRoot(item.node);
          }} />
      )}
      {confirmEl}
    </div>
  );
}
